import Cookies from 'js-cookie';
import { filterTestProfessionals, clearBackendTestAccess } from '../utils/testProfessional';

const TOKEN_KEY = 'daisies_auth_token';
const USER_KEY = 'daisies_user_data';
const LEAD_KEY = 'daisies_lead_data';
const API_URL = 'https://odoo.core.daisies.mx/marketing-manager/import-data';

// Obtener token de autenticación
export const getAuthToken = () => {
  return Cookies.get(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
};

// Guardar token
export const setAuthToken = (token) => {
  Cookies.set(TOKEN_KEY, token, { expires: 7 }); // Expira en 7 días
  localStorage.setItem(TOKEN_KEY, token);
};

// Eliminar token (logout)
export const removeAuthToken = () => {
  Cookies.remove(TOKEN_KEY);
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(LEAD_KEY);
  clearBackendTestAccess();
};

// Verificar si está autenticado
export const isAuthenticated = () => {
  return !!getAuthToken();
};

// Obtener datos del usuario
export const getUserData = () => {
  const userData = localStorage.getItem(USER_KEY);
  return userData ? JSON.parse(userData) : null;
};

// Guardar datos del usuario
export const setUserData = (userData) => {
  localStorage.setItem(USER_KEY, JSON.stringify(userData));
};

// Guardar datos del lead
export const setLeadData = (leadData) => {
  localStorage.setItem(LEAD_KEY, JSON.stringify(leadData));
};

// Obtener datos del lead
export const getLeadData = () => {
  const leadData = localStorage.getItem(LEAD_KEY);
  return leadData ? JSON.parse(leadData) : null;
};

// **PASO 1: Crear lead** (igual que en QuickBooking)
export const createLead = async (leadData, utmParams, selectedSlot, specialtyConfig, selectedSpecialty) => {
  try {
    const config = specialtyConfig[selectedSpecialty];
    if (!config) {
      throw new Error('Configuración de especialidad no encontrada');
    }

    const payload = {
      params: {
        res_model: "crm.lead",
        res_method: "create_lead_from_campaign",
        res_id: 1,
        res_params: {
          name: leadData.nombre,
          lastname: leadData.apellido,
          email: leadData.email,
          password: leadData.password,
          phone: leadData.telefono,
          country_id: leadData.countryPhoneId || 156, // res.country.id (MX=156); evita country_id 0
          country_code: leadData.phoneCode || '+52',
          pronoun_id: leadData.pronoun ? parseInt(leadData.pronoun, 10) : 1,
          birth_date: leadData.birthDate || false, // Odoo rompe con '' en campos Date; false = vacío
          birth_sex: leadData.birthSex,
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Mexico_City",
          utm_campaign: utmParams.campaign,
          utm_medium: utmParams.medium,
          utm_source: utmParams.source,
          schedule_id: selectedSlot.id,
          professional_ids: selectedSlot.professional_ids ? selectedSlot.professional_ids.map(p => p.id) : [selectedSlot.professional_ids[0].id],
          duration_id: selectedSlot?.duration_id || config.duration,
          specialty_id: config.id
        }
      }
    };
    const response = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    if (data.result?.success) {
      const leadId = data.result.lead_id;
      // Guardar datos del lead localmente
      setLeadData({
        leadId: leadId,
        email: leadData.email,
        password: leadData.password,
        userData: leadData
      });
      
      return { success: true, leadId };
    } else {
      const errorMsg = data.result?.error || 'Error desconocido al crear lead';
      return { success: false, error: errorMsg };
    }
  } catch (error) {
    return { success: false, error: 'Error de conexión. Intenta de nuevo.' };
  }
};

// **PASO 2: Convertir lead en usuario y obtener auth_token**
export const convertLeadToUser = async (leadId, email, password) => {
  try {
    const payload = {
      params: {
        res_model: "dais.user",
        res_method: "process_lead_to_user",
        res_id: 1,
        res_params: {
          lead_id: leadId,
          email: email.toLowerCase().trim(),
          password: password,
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Mexico_City"
        }
      }
    };
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    if (data.result?.success) {
      const { token, user } = data.result;
      // Guardar token y datos del usuario
      setAuthToken(token);
      setUserData({
        id: user.id,
        email: user.email,
        name: user.name,
        phone: user.phone,
        created_at: user.created_at
      });
      
      return { success: true, token, user };
    } else {
      const errorMsg = data.result?.error || 'Error desconocido al convertir lead en usuario';
      // Verificar si es error de credenciales
      const errorMsgLower = errorMsg.toLowerCase();
      let errorType = 'general';
      
      if (errorMsgLower.includes('email') || errorMsgLower.includes('correo')) {
        errorType = 'email';
      } else if (errorMsgLower.includes('password') || errorMsgLower.includes('contraseña')) {
        errorType = 'password';
      } else if (errorMsgLower.includes('lead') || errorMsgLower.includes('no encontrado')) {
        errorType = 'lead';
      }
      
      return { 
        success: false, 
        error: errorMsg,
        errorType
      };
    }
  } catch (error) {
    return { success: false, error: 'Error de conexión. Intenta de nuevo.' };
  }
};

// **Flujo completo: Crear lead + Convertir a usuario**
export const registerUserWithLeadFlow = async (userData, utmParams, selectedSlot, specialtyConfig, selectedSpecialty) => {
  try {
    // Paso 1: Crear lead
    const leadResult = await createLead(userData, utmParams, selectedSlot, specialtyConfig, selectedSpecialty);
    
    if (!leadResult.success) {
      return leadResult; // Retornar error del lead
    }
    
    // Paso 2: Convertir lead en usuario
    const userResult = await convertLeadToUser(
      leadResult.leadId, 
      userData.email, 
      userData.password
    );
    
    if (!userResult.success) {
      // Si falla la conversión, pero el lead se creó, podemos intentar login
      // Intentar login con las credenciales
      const loginResult = await login(userData.email, userData.password);
      
      if (loginResult.success) {
        return loginResult; // Si login funciona, retornar éxito
      }
      
      // Si login también falla, retornar error de conversión
      return userResult;
    }
    
    return userResult;
    
  } catch (error) {
    return { success: false, error: 'Error en el proceso de registro. Intenta de nuevo.' };
  }
};

// Mapping de códigos ISO de país → res.country.id en Odoo (country_phone_id).
// Default Mexico=156 si no encontramos match — la base original de Daisies está en MX.
const COUNTRY_CODE_TO_ODOO_ID = {
  'MX': 156,
  'US': 235,
  'CA': 39,
  'ES': 70,
  'AR': 11,
  'CO': 50,
};

export const createUserWithFirebaseUID = async ({
  email,
  firebase_uid,
  firebase_token, // Firebase ID token recién emitido — Odoo lo valida vía OAuth
  nombre,
  apellido,
  telefono,
  phoneCountry, // ISO code (ej. 'MX')
  countryPhoneId, // res.country.id real (preferido sobre el mapeo por ISO)
  birthDate,
  birthSex,
  pronoun,
  utmParams,
  selectedSlot,
  specialtyConfig,
  selectedSpecialty
}) => {
  try {
    const API_FIREBASE_URL = 'https://odoo.core.daisies.mx/odoo-firebase-core/odoo-import';

    // Paso 1: Crear usuario con Firebase UID.
    // El controller de Odoo rechaza con "Permission not allowed by oauth" si auth_token es null,
    // así que pasamos el Firebase ID token recién emitido al crear/loguear al usuario.
    const payloadStep1 = {
      params: {
        access_token: process.env.REACT_APP_ACCESS_TOKEN,
        account_id: parseInt(process.env.REACT_APP_ACCOUNT_ID, 10),
        auth_token: firebase_token || null,
        res_id: 1,
        res_model: "dais.user",
        res_method: "create_user_step_pending_email_verification",
        res_params: {
          email: email.toLowerCase().trim(),
          firebase_uid: firebase_uid
        }
      }
    };
    const responseStep1 = await fetch(API_FIREBASE_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${process.env.REACT_APP_ACCESS_TOKEN}`
      },
      body: JSON.stringify(payloadStep1)
    });

    const dataStep1 = await responseStep1.json();
    if (dataStep1.result?.success) {
      const userData = dataStep1.result.data;
      const authToken = dataStep1.result.auth_token;
      // Guardar token y datos del usuario
      setAuthToken(authToken);
      setUserData({
        id: userData.id,
        email: userData.email,
        name: nombre,
        phone: telefono,
        firebase_uid: firebase_uid,
        created_at: new Date().toISOString()
      });

      // Sincronizar phone + country_phone_id en Odoo.
      // CRÍTICO: `create_user_step_pending_email_verification` NO guarda phone ni
      // country_phone_id, y `update_user_profile` puede no aceptar el campo phone
      // (depende del DTO pydantic). Sin esto, `send_verification_code_to_phone` falla
      // o reporta success pero el provider de WhatsApp no tiene a quién enviarle.
      // Usamos el endpoint dedicado `change_user_mobile` que sí los guarda.
      if (telefono) {
        try {
          // Preferimos el id real que mandó el selector; el mapeo por ISO es solo respaldo.
          const resolvedCountryPhoneId = countryPhoneId || COUNTRY_CODE_TO_ODOO_ID[phoneCountry] || 156;
          const cleanPhone = String(telefono).replace(/\D/g, '');
          const mobilePayload = {
            params: {
              access_token: process.env.REACT_APP_ACCESS_TOKEN,
              account_id: parseInt(process.env.REACT_APP_ACCOUNT_ID, 10),
              auth_token: firebase_token || authToken,
              res_id: userData.id,
              res_model: "dais.user",
              res_method: "change_user_mobile",
              res_params: {
                mobile: cleanPhone,
                country_phone_id: resolvedCountryPhoneId,
              }
            }
          };
          const mobileRes = await fetch(API_FIREBASE_URL, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${process.env.REACT_APP_ACCESS_TOKEN}`
            },
            body: JSON.stringify(mobilePayload)
          });
          const mobileData = await mobileRes.json();
          console.log('[createUserWithFirebaseUID] change_user_mobile response:', mobileData);
          if (!mobileData.result?.success) {
            console.warn('[createUserWithFirebaseUID] change_user_mobile failed:', mobileData);
          }
        } catch (err) {
          console.warn('[createUserWithFirebaseUID] change_user_mobile error:', err);
        }
      }

      // Paso 2: Completar datos del usuario y crear lead si hay cita
      if (selectedSlot && selectedSpecialty) {
        try {
          const config = specialtyConfig[selectedSpecialty];
          if (config) {
            await completeUserProfileAndCreateLead({
              authToken,
              userData: {
                nombre,
                apellido,
                telefono,
                birthDate,
                birthSex,
                pronoun,
                email: email.toLowerCase().trim(),
                user_id: userData.id
              },
              utmParams,
              selectedSlot,
              specialtyConfig: config,
              selectedSpecialty
            });
          }
        } catch (leadError) {
          // Continuar aunque falle el lead
        }
      } else {
        // Solo completar perfil si no hay cita.
        // Fire-and-forget: el registro ya quedó persistido en Odoo y Firebase, el perfil
        // adicional se completa en background para no bloquear la UI (mejora la velocidad
        // percibida tras "Crear cuenta", ~1.5s ahorrados en condiciones normales).
        completeUserProfile(authToken, {
          nombre,
          apellido,
          telefono,
          birthDate,
          birthSex,
          pronoun,
          user_id: userData.id
        }).catch(err => {
          console.warn('[createUserWithFirebaseUID] background completeUserProfile failed:', err);
        });
      }
      
      return { success: true, user: userData, token: authToken };
    } else {
      // Los errores de Odoo pueden llegar en result.error o como excepción top-level en data.error
      const odooErr = dataStep1.error?.data?.message || dataStep1.error?.message;
      const errorMsg = dataStep1.result?.error || odooErr || 'Error desconocido al crear usuario';
      console.warn('[createUserWithFirebaseUID] Odoo error:', errorMsg, dataStep1);

      let errorType = 'general';
      const lowered = errorMsg.toLowerCase();
      if (lowered.includes('ya existe') || lowered.includes('already exists') || lowered.includes('duplicate')) {
        errorType = 'duplicate';
      } else if (lowered.includes('oauth') || lowered.includes('permission')) {
        errorType = 'oauth';
      }

      return {
        success: false,
        error: errorMsg,
        errorType
      };
    }
  } catch (error) {
    console.warn('[createUserWithFirebaseUID] Network error:', error);
    return {
      success: false,
      error: error?.message || 'Error de conexión. Intenta de nuevo.',
      errorType: 'connection'
    };
  }
};

// Función para completar perfil de usuario
const completeUserProfile = async (authToken, userData) => {
  try {
    const API_URL = 'https://odoo.core.daisies.mx/odoo-firebase-core/odoo-import';
    
    const payload = {
      params: {
        access_token: process.env.REACT_APP_ACCESS_TOKEN,
        account_id: parseInt(process.env.REACT_APP_ACCOUNT_ID, 10),
        auth_token: authToken,
        res_id: 1,
        res_model: "dais.user",
        res_method: "update_user_profile",
        res_params: {
          user_id: userData.user_id,
          nombre: userData.nombre,
          apellido: userData.apellido || '',
          telefono: userData.telefono,
          birth_date: userData.birthDate || false, // Odoo rompe con '' en campos Date; false = vacío
          birth_sex: userData.birthSex,
          pronoun_id: parseInt(userData.pronoun, 10) || 1
        }
      }
    };

    const response = await fetch(API_URL, {
      method: "POST",
      headers: { 
        "Content-Type": "application/json",
        "Authorization": `Bearer ${authToken}`
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    
    if (data.result?.success) {
      return { success: true };
    } else {
      return { success: false, error: data.result?.error };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const loginWithFirebase = async (email, firebase_uid) => {
  try {
    // El acceso a doctoras de prueba es por cuenta: reset al cambiar de sesión.
    clearBackendTestAccess();
    const API_URL = 'https://odoo.core.daisies.mx/odoo-firebase-core/odoo-import';
    
    const payload = {
      params: {
        access_token: process.env.REACT_APP_ACCESS_TOKEN,
        account_id: parseInt(process.env.REACT_APP_ACCOUNT_ID, 10),
        auth_token: null,
        res_id: 1,
        res_model: "dais.user",
        res_method: "login_with_firebase",
        res_params: {
          email: email.toLowerCase().trim(),
          firebase_uid: firebase_uid
        }
      }
    };

    const response = await fetch(API_URL, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${process.env.REACT_APP_ACCESS_TOKEN}`
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    if (data.result?.success) {
      const { token, user } = data.result;
      setAuthToken(token);
      setUserData({
        id: user.id,
        email: user.email,
        name: user.name,
        phone: user.phone,
        firebase_uid: firebase_uid,
        // user_type (dais_test / dais_intern) habilita ver doctoras de prueba.
        user_type: user.user_type ?? null,
        created_at: user.created_at
      });
      
      return { success: true, user, token };
    } else {
      const errorMsg = data.result?.error || 'Credenciales incorrectas';
      return { 
        success: false, 
        error: errorMsg,
        errorType: 'firebase_auth'
      };
    }
  } catch (error) {
    return { success: false, error: 'Error de conexión. Intenta de nuevo.' };
  }
};

// Función para completar perfil y crear lead para cita
const completeUserProfileAndCreateLead = async ({
  authToken,
  userData,
  utmParams,
  selectedSlot,
  specialtyConfig,
  selectedSpecialty
}) => {
  try {
    const API_URL = 'https://odoo.core.daisies.mx/odoo-firebase-core/odoo-import';
    
    const payload = {
      params: {
        access_token: process.env.REACT_APP_ACCESS_TOKEN,
        account_id: parseInt(process.env.REACT_APP_ACCOUNT_ID, 10),
        auth_token: authToken,
        res_id: 1,
        res_model: "dais.user",
        res_method: "complete_profile_and_create_lead",
        res_params: {
          user_id: userData.user_id,
          nombre: userData.nombre,
          apellido: userData.apellido || '',
          telefono: userData.telefono,
          birth_date: userData.birthDate || false, // Odoo rompe con '' en campos Date; false = vacío
          birth_sex: userData.birthSex,
          pronoun_id: parseInt(userData.pronoun, 10) || 1,
          email: userData.email,
          schedule_id: selectedSlot.id,
          professional_ids: selectedSlot.professional_ids ? 
            selectedSlot.professional_ids.map(p => p.id) : 
            [selectedSlot.professional_ids[0].id],
          duration_id: specialtyConfig.duration,
          specialty_id: specialtyConfig.id,
          utm_campaign: utmParams.campaign || '',
          utm_medium: utmParams.medium || '',
          utm_source: utmParams.source || '',
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Mexico_City"
        }
      }
    };
    const response = await fetch(API_URL, {
      method: "POST",
      headers: { 
        "Content-Type": "application/json",
        "Authorization": `Bearer ${authToken}`
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    if (data.result?.success) {
      // Guardar datos del lead si viene en la respuesta
      if (data.result.lead_id) {
        setLeadData({
          leadId: data.result.lead_id,
          email: userData.email,
          userData: userData
        });
      }
      
      return { success: true, leadId: data.result.lead_id };
    } else {
      // Intentar solo completar perfil si falla el lead
      await completeUserProfile(authToken, userData);
      
      return { 
        success: false, 
        error: data.result?.error,
        partialSuccess: true
      };
    }
  } catch (error) {
    // Intentar solo completar perfil
    await completeUserProfile(authToken, userData);
    
    return { 
      success: false, 
      error: error.message,
      partialSuccess: true 
    };
  }
};

// **Iniciar sesión** (para usuarios ya registrados) — usa Firebase Auth + Firestore
// Solicita el correo de restablecimiento de contraseña. Réplica del flujo de
// flutterUXS: Firebase Auth envía el enlace. Devuelve { success, error }.
export const requestPasswordReset = async (email) => {
  const clean = (email || '').trim();
  if (!clean.includes('@')) {
    return { success: false, error: 'Ingresa un correo electrónico válido.' };
  }
  try {
    const { sendPasswordResetInFirebase } = await import('./FirebaseService');
    return await sendPasswordResetInFirebase(clean);
  } catch (error) {
    return { success: false, error: 'Error de conexión. Intenta de nuevo.' };
  }
};

export const login = async (email, password) => {
  try {
    // El acceso a doctoras de prueba es por cuenta: reset al cambiar de sesión.
    clearBackendTestAccess();
    // 1. Autenticar con Firebase Auth
    const { loginUserInFirebase } = await import('./FirebaseService');
    const firebaseResult = await loginUserInFirebase(email, password);

    if (!firebaseResult.success) {
      // Mapear errores de Firebase a mensajes amigables
      let errorType = 'credentials';
      let errorMsg = 'Credenciales incorrectas';

      if (firebaseResult.errorCode === 'auth/user-not-found') {
        errorType = 'not_found';
        errorMsg = 'No se encontró una cuenta con este correo.';
      } else if (firebaseResult.errorCode === 'auth/wrong-password' || firebaseResult.errorCode === 'auth/invalid-credential') {
        errorType = 'password';
        errorMsg = 'La contraseña es incorrecta.';
      } else if (firebaseResult.errorCode === 'auth/invalid-email') {
        errorType = 'credentials';
        errorMsg = 'El correo electrónico no es válido.';
      } else if (firebaseResult.errorCode === 'auth/too-many-requests') {
        errorType = 'credentials';
        errorMsg = 'Demasiados intentos fallidos. Espera un momento antes de intentar de nuevo.';
      } else {
        errorMsg = firebaseResult.error || 'Error al iniciar sesión';
      }

      return { success: false, error: errorMsg, errorType };
    }

    // 2. Obtener perfil del usuario desde la colección "dais_user" en Firestore
    const { fetchDaisUserByFirebaseUid } = await import('./FirebaseService');
    
    let userProfile = null;
    try {
      userProfile = await fetchDaisUserByFirebaseUid(firebaseResult.firebaseUid);
    } catch (firestoreErr) {
      console.warn('No se pudo obtener perfil de dais_user:', firestoreErr);
    }

    const userData = {
      id: userProfile?.id || firebaseResult.firebaseUid,
      daisUserId: userProfile?.id || null,
      email: firebaseResult.email || email,
      name: userProfile?.name || '',
      nombre: userProfile?.name || '',
      apellido: userProfile?.last_name || '',
      phone: userProfile?.phone || '',
      telefono: userProfile?.phone || '',
      avatar: userProfile?.avatar || '',
      firebaseUid: firebaseResult.firebaseUid,
      birthDate: userProfile?.birth_date || '',
      birthSex: userProfile?.gender || '',
      pronoun: userProfile?.pronoun || '',
      // user_type (dais_test / dais_intern) habilita ver doctoras de prueba.
      user_type: userProfile?.user_type ?? userProfile?.userType ?? null,
      created_at: userProfile?.createdAt || new Date().toISOString()
    };

    // Guardar token y datos del usuario en el storage legacy
    setAuthToken(firebaseResult.firebaseToken);
    setUserData(userData);

    return { 
      success: true, 
      user: userData, 
      token: firebaseResult.firebaseToken,
      firebaseUid: firebaseResult.firebaseUid
    };
  } catch (error) {
    return { success: false, error: 'Error de conexión. Intenta de nuevo.' };
  }
};

// **Verificar disponibilidad de email** (usando servicio de Firebase)
export const checkEmailAvailability = async (email) => {
  try {
    // Importar dinámicamente para evitar circular dependencies
    const { checkEmailAvailability: firebaseCheck } = await import('./FirebaseService');
    return await firebaseCheck(email);
  } catch (error) {
    return true; // Asumir disponible si hay error
  }
};

// **Verificar disponibilidad de teléfono** (usando servicio de Firebase)
export const checkPhoneAvailability = async (phone) => {
  try {
    // Importar dinámicamente para evitar circular dependencies
    const { checkPhoneAvailability: firebaseCheck } = await import('./FirebaseService');
    return await firebaseCheck(phone);
  } catch (error) {
    return true; // Asumir disponible si hay error
  }
};

// **Cerrar sesión**
export const logout = () => {
  removeAuthToken();
  window.location.reload(); // Recargar para limpiar estado
};

// **Obtener doctoras** (requiere autenticación)
export const getDoctors = async (specialtyId, authToken) => {
  try {
    const API_DOCTORS_URL = 'https://odoo.core.daisies.mx/odoo-firebase-core/odoo-import';
    
    const payload = {
      params: {
        access_token: process.env.REACT_APP_ACCESS_TOKEN,
        account_id: process.env.REACT_APP_ACCOUNT_ID,
        auth_token: authToken,
        res_model: "dais.professional",
        res_method: "process_get_professional_with_calendar",
        res_params: {
          specialty_id: specialtyId,
          extra: {},
          user_id: null // Se obtendrá del auth_token
        }
      }
    };
    const response = await fetch(API_DOCTORS_URL, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    if (data.result?.success && data.result?.data) {
      // Doctoras de prueba: ocultas salvo en localhost o usuario interno.
      const doctors = filterTestProfessionals(Array.isArray(data.result.data) ? data.result.data : []);
      return { success: true, doctors };
    } else {
      return { success: false, error: data.result?.error || 'Error al obtener doctoras' };
    }
  } catch (error) {
    return { success: false, error: 'Error de conexión. Intenta de nuevo.' };
  }
};

